import {v4 as uuidv4} from 'uuid';
import {getEnv} from '../../common/env/env';
import {ItemSeriesDto} from '../../common/shared/api/gateway/ItemSeriesDto.interface';
import {PageContinueDto} from '../../common/shared/api/nicolas/vo/PageContinueDto.interface';
import {getDiscountRateText} from '../../common/shared/utils/getDiscountRateText';
import {getKageUrl} from '../../common/shared/utils/getKageUrl';
import {removeCategoryName} from '../../common/shared/utils/removeCategoryName';
import {SaleState, Series} from '../generated/types';
import {createAgeGrade} from './AgeGrade';
import {createAssetProperty} from './AssetProperty';
import {createBadgeType} from './BadgeType';
import {createBm} from './Bm';
import {createCategoryType} from './CategoryType';
import {createLocaleType} from './LocaleType';
import {createOnIssue} from './OnIssue';
import {createOperatorProperty} from './OperatorProperty';
import {createSaleMethod} from './SaleMethod';
import {createSaleState} from './SaleState';
import {createServiceProperty} from './ServiceProperty';
import {createSlideState} from './SlideState.enum';

interface Options {
  fallback_page_continue?: PageContinueDto;
  thumbnailResolution?: 'o1' | 'th1' | 'th2' | 'th3' | 'th4';
  thumbnailSize?: string;
  useStonKageUrl?: boolean;
}

export function createSeries(
  {
    age_grade,
    authors,
    business_model,
    category,
    category_uid,
    badge,
    is_all_free,
    state,
    is_waitfree,
    on_issue,
    free_slide_count,
    last_slide_added_dt,
    series_id,
    sub_category,
    sub_category_uid,
    thumbnail,
    land_thumbnail,
    title,
    pub_period,
    description,
    start_sale_dt,
    waitfree_block_count,
    waitfree_period_by_minute,
    sale_method,
    service_property,
    operator_property,
    asset_property,
    translate_property,
    lang,
  }: ItemSeriesDto,
  options?: Options
): Series {
  return {
    id: uuidv4(),
    seriesId: series_id,
    categoryType: createCategoryType(category_uid),
    title,
    lang: createLocaleType(lang),
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: options?.useStonKageUrl
            ? getEnv().stonKageUrlPrefix
            : getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: options?.thumbnailResolution ?? 'th3',
          size: options?.thumbnailSize,
        }),
    landThumbnail: land_thumbnail
      ? land_thumbnail.startsWith('http')
        ? land_thumbnail
        : getKageUrl({
            kageUrlPrefix: getEnv().kageUrlPrefix,
            kageId: land_thumbnail,
            resolution: options?.thumbnailResolution ?? 'th3',
          })
      : undefined,
    categoryUid: category_uid,
    category,
    subcategory: removeCategoryName(sub_category, category_uid),
    subcategoryUid: sub_category_uid,
    ageGrade: createAgeGrade(age_grade),
    authors,
    state: createSlideState(state),
    pubPeriod: pub_period,
    freeSlideCount: free_slide_count,
    lastSlideAddedDate: last_slide_added_dt,
    isAllFree: is_all_free,
    isWaitfree: is_waitfree,
    onIssue: createOnIssue(on_issue),
    badge: createBadgeType({nicolasBadgeTypeVo: {badge}}),
    waitfreeBlockCount: waitfree_block_count,
    waitfreePeriodByMinute: waitfree_period_by_minute,
    bm: business_model
      ? createBm({
          businessModel: business_model,
          isAllFree: is_all_free,
          isWaitfree: is_waitfree,
        })
      : undefined,
    saleState: state ? createSaleState(state) : SaleState.Unknown,
    description,
    startSaleDt: start_sale_dt,
    saleMethod: createSaleMethod(sale_method),
    serviceProperty: service_property
      ? createServiceProperty(
          service_property,
          options?.fallback_page_continue,
          series_id
        )
      : undefined,
    operatorProperty: operator_property
      ? createOperatorProperty(operator_property)
      : undefined,
    assetProperty: asset_property
      ? createAssetProperty(asset_property)
      : undefined,
    translateProperty: translate_property,
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
  };
}
